/**
 * Domain enums — defined once, consumed by the API (Prisma + validation), the
 * web app (chip labels, filter options) and the tests.
 *
 * Every value below comes from the backlog's stated states or the mocked FE's
 * existing value sets, so a label rendered in the UI and a value stored in the
 * database can never drift apart. The only additions are the `UNKNOWN` members,
 * which exist for the reason S04 gives: safe defaults when the analyzer returns
 * something we do not recognise.
 */
import { z } from 'zod';

/** Helper: build a Zod enum plus its readonly value tuple from one declaration. */
function domainEnum<const T extends readonly [string, ...string[]]>(values: T) {
  return { values, schema: z.enum(values) } as const;
}

// ---------------------------------------------------------------------------
// Contacts (S05)
// ---------------------------------------------------------------------------

/** Salutation the agent uses to address a contact. `NONE` means the agent uses the first name alone. */
export const Salutation = domainEnum(['MR', 'MS', 'MRS', 'MX', 'DR', 'PROF', 'NONE'] as const);
export type Salutation = z.infer<typeof Salutation.schema>;

// ---------------------------------------------------------------------------
// Calls (S07)
// ---------------------------------------------------------------------------

/**
 * Call outcome lifecycle: PENDING -> DIALING -> ANSWERED | MISSED | FAILED.
 *
 * Resolved from the platform's `disposition` on `session.ended` (R13). A
 * platform build that does not send one falls back to checking whether the
 * call's transcript contains a contact turn — deviation D3 in the plan.
 */
export const CallOutcome = domainEnum([
  'PENDING',
  'DIALING',
  'ANSWERED',
  'MISSED',
  'FAILED',
] as const);
export type CallOutcome = z.infer<typeof CallOutcome.schema>;

/**
 * How the platform says a call ended — the detail behind an outcome.
 *
 * Sirma's own values, upper-cased. Several of them share an outcome (NO_ANSWER
 * and BUSY are both MISSED; FAILED and PLATFORM_TERMINATED are both FAILED), and
 * this is what lets the operator see which. BUSY also covers a call the contact
 * rejected: the platform does not tell the two apart.
 */
export const CallDisposition = domainEnum([
  'COMPLETED',
  'NO_ANSWER',
  'BUSY',
  'FAILED',
  'PLATFORM_TERMINATED',
] as const);
export type CallDisposition = z.infer<typeof CallDisposition.schema>;

/** Where a call event came from, for the audit trail S07 requires. */
export const CallEventSource = domainEnum(['WEBHOOK', 'POLL', 'OPERATOR', 'WORKER'] as const);
export type CallEventSource = z.infer<typeof CallEventSource.schema>;

/** Who spoke a transcript turn. Sirma's `user` role is the contact. */
export const TranscriptRole = domainEnum(['AGENT', 'CONTACT'] as const);
export type TranscriptRole = z.infer<typeof TranscriptRole.schema>;

export const TranscriptSource = domainEnum(['SIRMA_SESSION', 'WEBHOOK'] as const);
export type TranscriptSource = z.infer<typeof TranscriptSource.schema>;

// ---------------------------------------------------------------------------
// Analysis results (S04, S08)
// ---------------------------------------------------------------------------

/**
 * Lead category. The first four are the backlog's fixed set and are the ones
 * offered as filter options in S08.
 *
 * `UNKNOWN` is never offered as a filter and never guessed at: each of the four
 * real categories is a claim about a person, so defaulting an unrecognised
 * analyzer value to any of them would fabricate one. Unknown values are stored
 * honestly, recorded in `mappingWarnings`, and surfaced as a SYSTEM alert.
 */
export const LeadCategory = domainEnum([
  'HOT_LEAD',
  'WARM_LEAD',
  'NOT_INTERESTED',
  'DO_NOT_CALL',
  'UNKNOWN',
] as const);
export type LeadCategory = z.infer<typeof LeadCategory.schema>;

/** The four categories S08's filter offers — `UNKNOWN` deliberately excluded. */
export const LEAD_CATEGORY_FILTERS = [
  'HOT_LEAD',
  'WARM_LEAD',
  'NOT_INTERESTED',
  'DO_NOT_CALL',
] as const satisfies readonly LeadCategory[];

export const UserIntent = domainEnum([
  'REQUEST_QUOTE',
  'REQUEST_EMAIL_INFO',
  'REQUEST_CALLBACK',
  'BOOK_VISIT',
  'NOT_INTERESTED',
  'OPT_OUT',
  'UNKNOWN',
] as const);
export type UserIntent = z.infer<typeof UserIntent.schema>;

/**
 * Recommended next action.
 *
 * `SEND_EMAIL` stays in the set even though nothing in this build sends email —
 * the change request removed that feature. It is a stored recommendation for a
 * human operator, and nothing acts on it.
 */
export const NextAction = domainEnum([
  'BOOK_APPOINTMENT',
  'SEND_EMAIL',
  'SCHEDULE_FOLLOW_UP',
  'MARK_DNC',
  'NO_ACTION',
] as const);
export type NextAction = z.infer<typeof NextAction.schema>;

/** Next actions that count as "needs a human to do something". */
export const ACTIONABLE_NEXT_ACTIONS = [
  'BOOK_APPOINTMENT',
  'SEND_EMAIL',
  'SCHEDULE_FOLLOW_UP',
] as const satisfies readonly NextAction[];

// ---------------------------------------------------------------------------
// Queue & scheduling (S06)
// ---------------------------------------------------------------------------

export const QueueStatus = domainEnum(['QUEUED', 'DIALING', 'DONE', 'CANCELLED'] as const);
export type QueueStatus = z.infer<typeof QueueStatus.schema>;

export const ScheduleStatus = domainEnum([
  'SCHEDULED',
  'DUE',
  'DISPATCHED',
  'CANCELLED',
  'FAILED',
] as const);
export type ScheduleStatus = z.infer<typeof ScheduleStatus.schema>;

// ---------------------------------------------------------------------------
// Import & export (S05, S08)
// ---------------------------------------------------------------------------

export const ImportStatus = domainEnum(['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED'] as const);
export type ImportStatus = z.infer<typeof ImportStatus.schema>;

export const ExportFormat = domainEnum(['CSV', 'XLSX'] as const);
export type ExportFormat = z.infer<typeof ExportFormat.schema>;

export const ExportStatus = domainEnum([
  'QUEUED',
  'GENERATING',
  'READY',
  'FAILED',
  'EXPIRED',
] as const);
export type ExportStatus = z.infer<typeof ExportStatus.schema>;

// ---------------------------------------------------------------------------
// Agents (S10)
// ---------------------------------------------------------------------------

export const AgentAuditAction = domainEnum([
  'SYNCED',
  'UPDATED',
  'PUSH_FAILED',
  'CONFLICT',
] as const);
export type AgentAuditAction = z.infer<typeof AgentAuditAction.schema>;

// ---------------------------------------------------------------------------
// Notifications (S12)
// ---------------------------------------------------------------------------

export const NotificationKind = domainEnum(['USAGE', 'LEAD', 'SYSTEM'] as const);
export type NotificationKind = z.infer<typeof NotificationKind.schema>;

/** Severity. Maps to the mocked FE's four levels: info / ok / warn / bad. */
export const NotificationSeverity = domainEnum(['INFO', 'OK', 'WARNING', 'CRITICAL'] as const);
export type NotificationSeverity = z.infer<typeof NotificationSeverity.schema>;

export const NotificationLinkType = domainEnum(['INTERACTION', 'CONTACT', 'NONE'] as const);
export type NotificationLinkType = z.infer<typeof NotificationLinkType.schema>;

// ---------------------------------------------------------------------------
// Webhook ingest (S04)
// ---------------------------------------------------------------------------

/**
 * The six event types Sirma's `/client/` webhook surface publishes: the five
 * confirmed against `backend`'s `WebhookEventType.kt`, plus
 * `audioagent.session.answered`, added with the disposition release (R13).
 */
export const SirmaWebhookEventType = domainEnum([
  'agent.execution.started',
  'agent.execution.completed',
  'agent.execution.failed',
  'audioagent.session.started',
  'audioagent.session.answered',
  'audioagent.session.ended',
] as const);
export type SirmaWebhookEventType = z.infer<typeof SirmaWebhookEventType.schema>;

export const WebhookDeliveryStatus = domainEnum([
  'RECEIVED',
  'PROCESSED',
  'REJECTED',
  'DUPLICATE',
] as const);
export type WebhookDeliveryStatus = z.infer<typeof WebhookDeliveryStatus.schema>;
